import { BOOKINGS_TAB_IDS, BookingsTabId } from 'components/bookings-tabs';
import { BOOKINGS_TAB_TTL_MS, StoredBookingsTab } from 'store/bookings-tab/consts';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isBookingsTabId = (value: unknown): value is BookingsTabId =>
  BOOKINGS_TAB_IDS.includes(value as BookingsTabId);

/**
 * The tab a stored entry names, or `null` if it names none worth restoring.
 *
 * Everything is checked rather than trusted. The entry lives on the HOST's origin, where the host
 * and anything else it frames can reach it, and arrives here over a postMessage bridge -- so it is
 * input, not state this app wrote. A tab id that is not one this build renders is the case that
 * actually happens: a tab removed in a later release, read back by a frame that still has the old
 * entry.
 *
 * ⚠️ NOT SCOPED TO A BUILDING, unlike the booking selection. A booking belongs to one building and
 * is meaningless in the next; "I was reading my past bookings" is true wherever the tenant is. The
 * scope would also cost this read the wait for the building fetch, for nothing.
 */
export const readStoredBookingsTab = (raw: string | null | undefined, now = Date.now()): BookingsTabId | null => {
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!isPlainObject(parsed)) return null;

    const { tab, savedAt } = parsed;

    if (!isBookingsTabId(tab)) return null;
    if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;

    /*
     * Bounded at BOTH ends. An upper bound alone lets a timestamp AHEAD of the clock read as fresh
     * for as long as it stays ahead -- which a device whose clock moved backwards after the write
     * produces on its own, with nobody tampering.
     */
    const age = now - savedAt;
    if (age < 0 || age > BOOKINGS_TAB_TTL_MS) return null;

    return tab;
  } catch {
    // The host hands back whatever is under the key; anything unparseable simply names no tab.
    return null;
  }
};

/** The entry to hand the host for a given tab. */
export const buildStoredBookingsTab = (tab: BookingsTabId, now = Date.now()): StoredBookingsTab => ({
  tab,
  savedAt: now,
});

/**
 * The tab a link names in its query, or `null` if it names none this build renders.
 *
 * ⚠️ `unknown`, not `string`, on purpose. `qs` answers an ARRAY for a param given twice and an
 * object for a bracketed one, so the value reaching here is only a string by convention. The guard
 * is the same one the stored entry goes through -- there is one set of tabs this app can render,
 * and both doors into it have to agree on what counts.
 *
 * No timestamp and no window, unlike the stored entry. A link is a request being made now; there is
 * nothing stale about it.
 */
export const readRequestedBookingsTab = (value: unknown): BookingsTabId | null =>
  isBookingsTabId(value) ? value : null;
